import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import axios from 'axios';

/** Safely pull a UOM id from likely fields on the container payload. */
function getContainerUomId(container) {
  // Adjust these field names if your API uses a different one.
  return (
    container?.uomId ??
    container?.uomInventoryId ??
    container?.unitOfMeasureId ??
    null
  );
}

/**
 * Decide capacity suffix from a UOM label string.
 * Rules:
 * - "each - A single, individual item"  -> hide Capacity (return null)
 * - "box - Box Container"               -> "ml"
 * - "L - A liter or litre"              -> "L"
 * - "Sack - A rectangular-shaped bag"   -> "g"
 * - "g - A gram"                        -> "g"
 * - "kg - kilogram"                     -> "kg"
 * - "pack - Items sold together"        -> "g"
 *
 * Fallback: "ml" if unrecognised label.
 */
function getCapacitySuffixFromLabel(label) {
  if (!label) return 'ml';
  const uom = String(label).trim().toLowerCase();

  if (uom.startsWith('each')) return null;
  if (uom.startsWith('box')) return 'g';
  if (uom === 'l' || uom.startsWith('l -')) return 'ml';
  if (uom.startsWith('sack')) return 'g';
  if (uom === 'g' || uom.startsWith('g -')) return 'g';
  if (uom === 'kg' || uom.startsWith('kg')) return 'kg';
  if (uom.startsWith('pack')) return 'g';

  return 'ml';
}

const ContainerTypes = ({ setActiveView }) => {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uomLoading, setUomLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const fetchContainerTypes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('/container-types', {
        withCredentials: true,
      });

      if (!response.data?.success) {
        throw new Error('Failed to load container types');
      }

      const baseContainers = response.data.data ?? [];

      // Build a unique list of UOM IDs to fetch their labels.
      const uniqueUomIds = Array.from(
        new Set(
          baseContainers
            .map(getContainerUomId)
            .filter(Boolean)
        )
      );

      // Map uomId -> label
      const uomLabelMap = {};
      if (uniqueUomIds.length > 0) {
        setUomLoading(true);
        try {
          const results = await Promise.all(
            uniqueUomIds.map(async (id) => {
              try {
                const r = await axios.get(`/uoms/${id}`, { withCredentials: true });
                // Expecting { success: true, data: { id, label, ... } }
                const label = r?.data?.data?.label ?? null;
                return [id, label];
              } catch (e) {
                // If a single UOM fetch fails, keep going; we’ll just miss the label and fallback
                return [id, null];
              }
            })
          );
          results.forEach(([id, label]) => {
            uomLabelMap[id] = label;
          });
        } finally {
          setUomLoading(false);
        }
      }

      // Enrich containers with uomLabel for rendering rules
      const enriched = baseContainers.map((c) => {
        const uomId = getContainerUomId(c);
        const uomLabel = uomId ? uomLabelMap[uomId] ?? null : null;
        return { ...c, uomLabel };
      });

      setContainers(enriched);
    } catch (err) {
      console.error('Error fetching container types:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load container types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchContainerTypes();
      setActiveView?.('containerTypes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleAddContainer = () => {
    navigate('/dashboard/container-types/add');
  };

  const handleContainerClick = (id) => {
    navigate(`/dashboard/container-types/${id}`);
  };

  if (!isAuthenticated) return null;

  const showOverallSpinner = loading || uomLoading;

  return (
    <div className="content-container">
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="d-flex justify-content-end mb-4">
        {(user?.userGroup === 0 || user?.userGroup === 1) && (
          <Button variant="primary" onClick={handleAddContainer}>
            Add New Container
          </Button>
        )}
      </div>

      {showOverallSpinner ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          {uomLoading && <div className="mt-2 small text-muted">Loading UOM details…</div>}
        </div>
      ) : (
        <div className="container-grid-container">
          {containers.length > 0 ? (
            <Row className="g-4">
              {containers.map((container) => {
                const hasValue =
                  container?.measureMax !== undefined && container?.measureMax !== null;

                const suffix = getCapacitySuffixFromLabel(container?.uomLabel);

                return (
                  <Col
                    key={container.id}
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2}
                    xl={2}
                    xxl={2}
                  >
                    <Card
                      className="h-100 container-card shadow-sm"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleContainerClick(container.id)}
                    >
                      <Card.Body className="d-flex flex-column align-items-center justify-content-start text-center">
                        <div className="mb-2 w-100" style={{ minHeight: '3rem' }}>
                          <h6 className="text-wrap fw-bold m-0">
                            {container.friendlyName || container.containerTypeName}
                          </h6>
                        </div>
                        <div className="container-image-container mb-2">
                          <img
                            src={container.imageURL || '/placeholder-container.png'}
                            alt={
                              container.friendlyName ||
                              container.containerTypeName ||
                              'Container image'
                            }
                            className="container-image img-fluid"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder-container.png';
                            }}
                            style={{
                              maxHeight: '150px',
                              objectFit: 'contain',
                              borderRadius: '8px',
                            }}
                          />
                        </div>

                        {/* Capacity (UOM-aware via fetched label). Hide for "each". */}
                        {suffix && hasValue ? (
                          <div className="mt-1 text-center">
                            <small>Capacity: {container.measureMax}{suffix}</small>
                          </div>
                        ) : null}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="text-center py-5">No container types found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContainerTypes;